/**
 * QuestionAssetManager Component
 *
 * Manages media assets for questions:
 * - Main asset (photo/video/audio/PDF) via CreateAssetBlock
 * - Correct answer asset (audio) for NORMAL_SCORE question types
 * - Video AI session URL for STORY test types
 */

import React, { useState } from 'react';
import { Button, Input, Row, Typography, Upload, message } from 'antd';
import type { UploadProps, UploadFile } from 'antd';
import { CloseCircleFilled, UploadOutlined } from '@ant-design/icons';
import { CreateAssetBlock } from '../../../components';
import { Asset, AssetType, TestTypes } from '../../../graphql/API';
import { StorageService, UploadFileType } from '../../Storage/StorageService';
import { QUESTION_TYPE_DICT } from '../QuestionTypeDict';

const { Text } = Typography;
const storageService = new StorageService();

export interface QuestionAssetManagerProps {
  // Main asset
  asset: Asset | undefined;
  onAssetChange: (asset: Asset) => void;

  // Correct answer asset (for NORMAL_SCORE questions)
  correctAsset?: Asset | undefined;
  onCorrectAssetChange?: (asset: Asset) => void;

  // Video AI session URL (for STORY test type)
  videoAISessionUri?: string | null;
  onVideoAISessionUriChange?: (uri: string | null) => void;

  // Question type and test type
  questionType: string;
  testType?: TestTypes;
}

/**
 * Component for managing question assets (main, correct answer, video AI session)
 */
export const QuestionAssetManager: React.FC<QuestionAssetManagerProps> = ({
  asset,
  onAssetChange,
  correctAsset,
  onCorrectAssetChange,
  videoAISessionUri,
  onVideoAISessionUriChange,
  questionType,
  testType,
}) => {
  const [fileAudio, setFileAudio] = useState<UploadFile | null>(null);
  const [fileSummaryVideo, setFileSummaryVideo] = useState<UploadFile | null>(null);
  const [uploading, setUploading] = useState(false);

  // Determine if this question type requires correct answer asset (NORMAL_SCORE)
  const needsCorrectAsset =
    QUESTION_TYPE_DICT[questionType] === 'NORMAL_SCORE' && onCorrectAssetChange;

  // Determine if this is a STORY test type (needs video AI session)
  const needsVideoAISession = testType === TestTypes.STORY && onVideoAISessionUriChange;

  /**
   * Audio upload props (for correct answer asset)
   */
  const propsAudioUpload: UploadProps = {
    type: 'select',
    accept: '.aac',
    listType: 'text',
    multiple: false,
    maxCount: 1,
    onRemove: () => {
      setFileAudio(null);
    },
    beforeUpload: (file) => {
      setFileAudio(file);
      return false;
    },
  };

  /**
   * Summary video upload props (for video AI session)
   */
  const propsSummaryVideo: UploadProps = {
    type: 'select',
    accept: '.mp4,.aac',
    listType: 'text',
    multiple: false,
    maxCount: 1,
    onRemove: () => {
      setFileSummaryVideo(null);
    },
    beforeUpload: (file) => {
      setFileSummaryVideo(file);
      return false;
    },
  };

  /**
   * Handles file upload for correct answer asset or video AI session
   */
  const handleUpload = async (
    fileUri: UploadFile,
    typeUpload: UploadFileType
  ) => {
    setUploading(true);
    try {
      let filePath: string;

      switch (typeUpload) {
        case UploadFileType.TEST_MEDIA:
          // Upload correct answer audio
          filePath = await storageService.uploadFile(fileUri, typeUpload, 'test/media');
          const tempAsset = {
            fileUri: filePath,
            type: AssetType.AUDIO,
            thumbnailUri: '',
          } as Asset;
          if (onCorrectAssetChange) {
            onCorrectAssetChange(tempAsset);
          }
          break;

        case UploadFileType.TEST_MEDIA_VIDEO_SESSION:
          // Upload video AI session
          filePath = await storageService.uploadFile(fileUri, typeUpload, 'test/media');
          if (onVideoAISessionUriChange) {
            onVideoAISessionUriChange(filePath);
          }
          break;

        default:
          break;
      }

      message.success('Upload successfully.');
      setUploading(false);
    } catch (error) {
      console.error('Upload error:', error);
      setUploading(false);
      message.error('Upload failed.');
    }
  };

  return (
    <div>
      {/* Main Asset */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text style={{ fontSize: '20px', fontWeight: 'bold', display: 'block' }}>
          Question asset
        </Text>
        <CreateAssetBlock
          asset={asset}
          defaultAssetType={asset?.type ?? AssetType.PHOTO}
          onChange={(selectedAsset) => onAssetChange(selectedAsset)}
        />
      </div>

      {/* Correct Answer Asset (for NORMAL_SCORE questions) */}
      {needsCorrectAsset && (
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <Text style={{ fontSize: '20px', fontWeight: 'bold', display: 'block' }}>
            Question correct answer asset file url
          </Text>

          {correctAsset?.fileUri ? (
            <Row
              style={{
                display: 'flex',
                alignItems: 'center',
                alignContent: 'center',
              }}
            >
              <Input
                style={{ width: '80%' }}
                value={correctAsset.fileUri}
                readOnly
                placeholder="Correct answer url"
              />
              <CloseCircleFilled
                style={{ marginInline: 20, cursor: 'pointer' }}
                onClick={() => {
                  setFileAudio(null);
                  const emptyAsset = {
                    fileUri: '',
                    type: AssetType.AUDIO,
                    thumbnailUri: '',
                  } as Asset;
                  onCorrectAssetChange!(emptyAsset);
                }}
              />
            </Row>
          ) : (
            <Row>
              <Upload {...propsAudioUpload}>
                <Button disabled={!!fileAudio} icon={<UploadOutlined />}>
                  Select File
                </Button>
              </Upload>

              <Button
                type="primary"
                onClick={() => handleUpload(fileAudio!, UploadFileType.TEST_MEDIA)}
                disabled={!fileAudio}
                loading={uploading}
              >
                {uploading ? 'Uploading' : 'Upload'}
              </Button>
            </Row>
          )}
        </div>
      )}

      {/* Video AI Session URL (for STORY test type) */}
      {needsVideoAISession && (
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <Text style={{ display: 'block' }}>Video AI Session url</Text>

          {videoAISessionUri ? (
            <Row
              style={{
                display: 'flex',
                alignItems: 'center',
                alignContent: 'center',
              }}
            >
              <Input
                style={{ width: '80%' }}
                value={videoAISessionUri}
                readOnly
                placeholder="Video AI session URL"
              />
              <CloseCircleFilled
                style={{ marginInline: 20, cursor: 'pointer' }}
                onClick={() => {
                  onVideoAISessionUriChange!(null);
                  setFileSummaryVideo(null);
                }}
              />
            </Row>
          ) : (
            <Row>
              <Upload {...propsSummaryVideo}>
                <Button disabled={!!fileSummaryVideo} icon={<UploadOutlined />}>
                  Select File
                </Button>
              </Upload>

              <Button
                type="primary"
                onClick={() =>
                  handleUpload(fileSummaryVideo!, UploadFileType.TEST_MEDIA_VIDEO_SESSION)
                }
                disabled={!fileSummaryVideo}
                loading={uploading}
              >
                {uploading ? 'Uploading' : 'Start Upload'}
              </Button>
            </Row>
          )}
        </div>
      )}
    </div>
  );
};
